@if (cartService.isTrackingOpen() && activeOrders().length > 0) {
<!-- Backdrop -->
<div (click)="close()" class="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs transition-opacity duration-300 flex items-center justify-center p-4 sm:p-6">
  <!-- Modal Card -->
  <div (click)="$event.stopPropagation()"
    class="w-full max-w-md bg-surface rounded-2xl shadow-2xl border border-border overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
    <!-- Header with Close Button -->
    <div class="flex items-center justify-between px-5 py-3.5 border-b border-border bg-surface shrink-0">
      <div class="flex items-center gap-2">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">
          {{ 'TRACKING.TITLE' | translate }}
        </span>
        @if (activeOrders().length > 1) {
        <span class="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">
          {{ 'TRACKING.ACTIVE_ORDERS_COUNT' | translate: { count: activeOrders().length } }}
        </span>
        } @else if (order()) {
        <span class="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">
          {{ getOrderTabLabel(order()!, 0) }}
          @if (order()?.CheckNo) {
          <span class="opacity-75">({{ order()?.CheckNo }})</span>
          }
        </span>
        }
      </div>
      <button (click)="close()" class="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:text-main hover:bg-surface-hover transition-colors cursor-pointer"
        aria-label="Close">
        <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Multi-Order Tabs Selector (when activeOrders > 1) -->
    @if (activeOrders().length > 1) {
    <div class="px-4 py-2.5 bg-surface-hover/60 border-b border-border flex items-center gap-2 overflow-x-auto no-scrollbar shrink-0">
      @for (ord of activeOrders(); track ord.id; let i = $index) {
      <button type="button" (click)="selectOrder(ord.id!)"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer border" [ngClass]="selectedOrderId() === ord.id
                ? 'bg-surface text-main border-border shadow-xs ring-1 ring-primary/20'
                : 'bg-transparent text-muted border-transparent hover:text-main hover:bg-surface/50'">
        <span class="w-2 h-2 rounded-full shrink-0" [ngClass]="getStatusDotClass(ord.Status)"></span>
        <span class="font-medium">
          {{ getOrderTabLabel(ord, i) }}
          @if (ord.CheckNo) {
          <span class="opacity-75">({{ ord.CheckNo }})</span>
          }
        </span>
        <span class="text-[11px] font-bold text-main shrink-0">
          {{ ord.PayableAmount | formatPrice:currencyPrecision:companyCurrency }}
        </span>
      </button>
      }
    </div>
    }

    <!-- Scrollable Body -->
    <div class="overflow-y-auto p-5 space-y-5">
      <!-- STATUS 0: PENDING -->
      @if (status() === OnlineOrderStatus.PENDING) {
      <div class="flex flex-col items-center text-center p-6 rounded-2xl bg-amber-500/10 border border-amber-500/20 space-y-3">
        <div class="relative flex items-center justify-center">
          <div class="w-16 h-16 rounded-full bg-amber-500/20 animate-ping absolute"></div>
          <div class="w-16 h-16 rounded-full bg-amber-500 text-white flex items-center justify-center relative shadow-lg">
            <svg class="w-8 h-8 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
          </div>
        </div>

        <div class="space-y-1">
          <h3 class="text-base font-bold text-amber-900">
            {{ 'TRACKING.PENDING_TITLE' | translate }}
          </h3>
          <p class="text-xs text-amber-800 max-w-xs">
            {{ 'TRACKING.PENDING_DESC' | translate }}
          </p>
        </div>
      </div>
      }

      <!-- STATUS 1 & 2: ACCEPTED / PREPARING -->
      @if (status() === OnlineOrderStatus.ACCEPTED || status() === OnlineOrderStatus.PREPARING) {
      <div class="flex flex-col items-center text-center p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 space-y-3">
        <div class="w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30">
          <svg class="w-8 h-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
        </div>

        <div class="space-y-1">
          <h3 class="text-base font-bold text-emerald-900">
            {{ (status() === OnlineOrderStatus.PREPARING ? 'TRACKING.PREPARING_TITLE' : 'TRACKING.ACCEPTED_TITLE') | translate }}
          </h3>
          <p class="text-xs text-emerald-800">
            {{ 'TRACKING.ACCEPTED_DESC' | translate }}
          </p>
        </div>

        <!-- Estimated Ready Time Pill -->
        @if (formattedReadyTime()) {
        <div class="mt-2 w-full p-3 bg-surface rounded-xl border border-emerald-500/30 shadow-xs flex items-center justify-around">
          <div class="text-left">
            <span class="text-[11px] text-muted block uppercase tracking-wider font-semibold">
              {{ 'TRACKING.ESTIMATED_READY' | translate }}
            </span>
            <span class="text-lg font-extrabold text-emerald-700">
              {{ formattedReadyTime() }}
            </span>
          </div>

          @if (remainingMinutes() !== null) {
          <div class="text-right">
            <span class="text-[11px] text-muted block uppercase tracking-wider font-semibold">
              {{ 'TRACKING.TIME_LEFT' | translate }}
            </span>
            <span class="text-sm font-bold text-emerald-800">
              ~{{ remainingMinutes() }} min
            </span>
          </div>
          }
        </div>
        }
      </div>
      }

      <!-- STATUS 3: READY -->
      @if (status() === OnlineOrderStatus.READY) {
      <div class="flex flex-col items-center text-center p-6 rounded-2xl bg-green-500/10 border border-green-500/20 space-y-3">
        <div class="w-16 h-16 rounded-full bg-green-600 text-white flex items-center justify-center shadow-lg">
          <svg class="w-8 h-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
        </div>
        <div class="space-y-1">
          <h3 class="text-base font-bold text-green-900">
            {{ 'TRACKING.READY_TITLE' | translate }}
          </h3>
          <p class="text-xs text-green-800">
            {{ 'TRACKING.READY_DESC' | translate }}
          </p>
        </div>
      </div>
      }

      <!-- STATUS 90: REJECTED -->
      @if (status() === OnlineOrderStatus.REJECTED) {
      <div class="flex flex-col items-center text-center p-6 rounded-2xl bg-red-500/10 border border-red-500/20 space-y-3">
        <div class="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg shadow-red-500/30">
          <svg class="w-8 h-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </div>

        <div class="space-y-1">
          <h3 class="text-base font-bold text-red-900">
            {{ 'TRACKING.REJECTED_TITLE' | translate }}
          </h3>
          <p class="text-xs text-red-800">
            {{ 'TRACKING.REJECTED_DESC' | translate }}
          </p>
        </div>

        @if (order()?.RejectionReason) {
        <div class="w-full p-3 rounded-xl bg-surface border border-red-200 text-left text-xs text-red-700 space-y-1 mt-1">
          <span class="font-bold text-[10px] uppercase tracking-wider text-red-500 block">
            {{ 'TRACKING.REASON_LABEL' | translate }}:
          </span>
          <p class="font-medium">{{ order()?.RejectionReason }}</p>
        </div>
        }
      </div>
      }

      <!-- Order Level Note (if exists) -->
      @if (order()?.Description) {
      <div class="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-xs space-y-1">
        <span class="font-bold text-[10px] uppercase tracking-wider text-amber-800 block">
          {{ 'CART.ORDER_NOTES' | translate }}:
        </span>
        <p class="text-amber-950 font-medium italic">"{{ order()?.Description }}"</p>
      </div>
      }

      <!-- Order Items Breakdown -->
      <div class="space-y-2 border-t border-border pt-4">
        <h4 class="text-xs font-bold uppercase tracking-wider text-muted">
          {{ 'TRACKING.ITEMS_ORDERED' | translate }}
        </h4>
        <div class="space-y-2">
          @for (item of order()?.OrderItems; track $index) {
          <div class="flex justify-between items-start text-xs">
            <div>
              <span class="font-semibold text-main">{{ item.Quantity }}x {{ getDisplayText(item.Name) }}</span>
              @if (getItemFeatures(item.OrderItemFeature).length > 0) {
              <div class="text-[11px] text-muted pl-2 space-y-0.5 mt-0.5">
                @for (f of getItemFeatures(item.OrderItemFeature); track $index) {
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-1 min-w-0">
                    @if (f.Type === OptionType.REMOVABLE) {
                    <span class="text-amber-700 dark:text-amber-400 font-semibold shrink-0">[{{ 'OPTION.REMOVED_PREFIX' | translate }}]</span>
                    <span class="truncate line-through">{{ getDisplayText(f.Name) }}</span>
                    } @else if (f.Type === OptionType.SIZE) {
                    <span class="truncate font-medium text-main">{{ getDisplayText(f.Name) }}</span>
                    } @else if (f.Type === OptionType.MULTIPLY) {
                    <span class="truncate text-main">{{ getDisplayText(f.Name) }}</span>
                    } @else if (f.Type === OptionType.DISCOUNT_PERCENTAGE || f.Type === OptionType.DISCOUNT_AMOUNT) {
                    <span class="truncate text-emerald-600 dark:text-emerald-400 font-medium">{{ getDisplayText(f.Name) }}</span>
                    } @else {
                    <span class="truncate">+ {{ getDisplayText(f.Name) }}{{ (f.Quantity && f.Quantity > 1) ? ' (x' + f.Quantity + ')' : '' }}</span>
                    }
                  </div>

                  <div class="shrink-0 ml-2">
                    @if (f.Type === OptionType.MULTIPLY) {
                    <span class="font-semibold text-primary">x{{ formatMultiplier(f.Modifier) }}</span>
                    } @else if (f.Type === OptionType.DISCOUNT_PERCENTAGE) {
                    <span class="font-bold text-emerald-600 dark:text-emerald-400">-%{{ formatMultiplier(f.Modifier) }}%</span>
                    } @else if (f.Type === OptionType.DISCOUNT_AMOUNT) {
                    <span class="font-bold text-emerald-600 dark:text-emerald-400">-{{ f.Modifier | formatPrice:currencyPrecision:companyCurrency }}</span>
                    } @else if (f.Modifier && f.Modifier > 0) {
                    <span class="text-primary font-medium">+{{ (f.Modifier * (f.Quantity || 1)) | formatPrice:currencyPrecision:companyCurrency }}</span>
                    }
                  </div>
                </div>
                }
              </div>
              }
              @if (item.Description) {
              <div class="text-[11px] text-muted italic pl-2">"{{ item.Description }}"</div>
              }
            </div>
            <span class="font-bold text-main shrink-0 ml-2">
              {{ item.OrderItemTotalPayable | formatPrice:currencyPrecision:companyCurrency }}
            </span>
          </div>
          }
        </div>

        <div class="pt-2 border-t border-border flex justify-between items-center text-sm font-bold text-main">
          <span>{{ 'CART.TOTAL' | translate }}</span>
          <span class="text-primary">{{ order()?.PayableAmount | formatPrice:currencyPrecision:companyCurrency }}</span>
        </div>
      </div>

      <!-- Combined Total Summary (when activeOrders > 1) -->
      @if (activeOrders().length > 1) {
      <div class="p-3.5 rounded-xl bg-surface-hover/70 border border-border flex items-center justify-between text-xs mt-2">
        <span class="font-semibold text-muted">
          {{ 'TRACKING.TOTAL_ALL_ORDERS' | translate }} ({{ activeOrders().length }})
        </span>
        <span class="text-sm font-extrabold text-primary">
          {{ totalAllActiveOrders() | formatPrice:currencyPrecision:companyCurrency }}
        </span>
      </div>
      }
    </div>

    <!-- Seated Table Quick Actions inside Tracking Modal -->
    @if (waiterService.isTableCallAvailable()) {
    <div class="px-4 py-2.5 bg-background border-t border-border flex items-center gap-2">
      <!-- Call Waiter -->
      <button type="button" (click)="waiterService.callWaiter()" [disabled]="waiterService.isBusy()"
        class="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer border shadow-2xs active:scale-95 disabled:active:scale-100"
        [ngClass]="{
          'bg-amber-500 hover:bg-amber-600 text-white border-amber-500': !waiterService.isBusy(),
          'bg-amber-100 text-amber-900 border-amber-300 opacity-80 cursor-wait': waiterService.isSubmitting() && waiterService.submittingType() === NotificationType.TABLE_CALL,
          'bg-surface-hover text-muted border-border opacity-70 cursor-not-allowed': waiterService.isBusy() && !(waiterService.isSubmitting() && waiterService.submittingType() === NotificationType.TABLE_CALL)
        }">
        @if (waiterService.isSubmitting() && waiterService.submittingType() === NotificationType.TABLE_CALL) {
        <svg class="w-3.5 h-3.5 animate-spin shrink-0 text-amber-800" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>{{ 'WAITER.CALLING' | translate }}</span>
        } @else if (waiterService.isCooldownActive()) {
        <svg class="w-3.5 h-3.5 shrink-0 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <span class="tabular-nums">{{ 'WAITER.WAIT_COOLDOWN' | translate: { seconds: waiterService.cooldownSeconds() } }}</span>
        } @else {
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
        </svg>
        <span class="truncate">{{ 'WAITER.CALL_WAITER' | translate }}</span>
        }
      </button>

      <!-- Request Bill -->
      <button type="button" (click)="waiterService.requestBill()" [disabled]="waiterService.isBusy()"
        class="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer border shadow-2xs active:scale-95 disabled:active:scale-100"
        [ngClass]="{
          'bg-primary hover:opacity-90 text-primary-foreground border-primary': !waiterService.isBusy(),
          'bg-primary/20 text-primary border-primary/40 opacity-80 cursor-wait': waiterService.isSubmitting() && waiterService.submittingType() === NotificationType.BILL_REQUEST,
          'bg-surface-hover text-muted border-border opacity-70 cursor-not-allowed': waiterService.isBusy() && !(waiterService.isSubmitting() && waiterService.submittingType() === NotificationType.BILL_REQUEST)
        }">
        @if (waiterService.isSubmitting() && waiterService.submittingType() === NotificationType.BILL_REQUEST) {
        <svg class="w-3.5 h-3.5 animate-spin shrink-0 text-primary" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>{{ 'WAITER.REQUESTING' | translate }}</span>
        } @else if (waiterService.isCooldownActive()) {
        <svg class="w-3.5 h-3.5 shrink-0 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <span class="tabular-nums">{{ 'WAITER.WAIT_COOLDOWN' | translate: { seconds: waiterService.cooldownSeconds() } }}</span>
        } @else {
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <span class="truncate">{{ 'WAITER.REQUEST_BILL' | translate }}</span>
        }
      </button>
    </div>
    }

    <!-- Footer Actions -->
    <div class="p-4 border-t border-border bg-surface shrink-0 flex gap-2">
      <button type="button" (click)="close()"
        class="flex-1 py-2.5 px-4 rounded-xl bg-surface-hover hover:bg-border text-main font-semibold text-xs border border-border transition-colors text-center cursor-pointer">
        {{ 'TRACKING.ORDER_MORE' | translate }}
      </button>

      @if (status() === OnlineOrderStatus.REJECTED || status() === OnlineOrderStatus.DELIVERED || status() === OnlineOrderStatus.READY) {
      <button type="button" (click)="dismiss()"
        class="flex-1 py-2.5 px-4 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold text-xs transition-colors text-center cursor-pointer">
        {{ 'TRACKING.DISMISS' | translate }}
      </button>
      }
    </div>
  </div>
</div>
}